import { Input, Text } from '@viapeople/components'
import type React from 'react'
import { useState } from 'react'
import { FormLabelWithTooltip } from './FormLabelWithTooltip'
import type { ComponentProps, NumberFieldCustomProps } from './types'

type FormsNumberFieldProps = ComponentProps & {
	value?: string
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
	readOnly?: boolean
	hasError?: boolean
}

export function FormsNumberField({ component, value, onChange, readOnly, required, hasError }: FormsNumberFieldProps) {
	const { label, labelColor, labelSize, labelWeight, prefix, suffix, decimals, tooltipEnabled, tooltipText } =
		component.custom as NumberFieldCustomProps
	const [internalValue, setInternalValue] = useState<string>('')

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value
		let newValue: string

		if (val !== '') {
			const numVal = Number.parseFloat(val)
			if (!Number.isNaN(numVal)) {
				newValue = decimals ? numVal.toFixed(decimals) : val
			} else {
				return
			}
		} else {
			newValue = ''
		}

		const newEvent = {
			...e,
			target: {
				...e.target,
				value: newValue,
			},
		}
		onChange?.(newEvent)

		if (value === undefined) {
			setInternalValue(newValue)
		}
	}

	return (
		<div className='flex flex-col gap-1'>
			{label && (
				<FormLabelWithTooltip
					label={label}
					labelColor={labelColor}
					labelSize={labelSize}
					labelWeight={labelWeight}
					required={required}
					readOnly={readOnly}
					tooltipEnabled={tooltipEnabled}
					tooltipText={tooltipText}
				/>
			)}
			<div className='flex items-center gap-2 self-start'>
				{prefix && <Text>{prefix}</Text>}
				{readOnly ? (
					<div className='bg-neutral-50 border border-neutral-200 rounded-md px-4 py-2'>
						<Text>{value}</Text>
					</div>
				) : (
					<Input
						id={component.id}
						type='number'
						value={value !== undefined ? value : internalValue}
						onChange={handleChange}
						style={{ width: '100px' }}
						variant={hasError ? 'error' : 'default'}
					/>
				)}
				{suffix && <Text>{suffix}</Text>}
			</div>
		</div>
	)
}
